/**
 * Authentication Functions
 * Handles tenant admin and platform admin login and authentication
 * 
 * Updated: 2024-12-19
 * - Migrated from client-side SHA-256 to server-side bcrypt via Edge Functions
 * - Maintains backward compatibility with existing SHA-256 hashes
 */

import { getDb } from './db'
import { getSupabaseClient } from './supabase'

/**
 * Gets Supabase URL and anon key for Edge Function calls
 */
function getSupabaseConfig() {
  const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL
  const supabaseAnonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Supabase URL and anon key must be configured')
  }

  return { supabaseUrl, supabaseAnonKey }
}

/**
 * Hashes a password using bcrypt via Supabase Edge Function
 * Falls back to SHA-256 if Edge Function is not available (backward compatibility)
 * 
 * @param password - Plain text password to hash
 * @returns Hashed password (bcrypt or SHA-256)
 */
async function hashPassword(password: string): Promise<string> {
  try {
    // Try Edge Function first (preferred method)
    const { supabaseUrl, supabaseAnonKey } = getSupabaseConfig()
    
    const response = await fetch(`${supabaseUrl}/functions/v1/hash-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'apikey': supabaseAnonKey
      },
      body: JSON.stringify({ password })
    })

    if (response.ok) {
      const { hashedPassword } = await response.json()
      if (hashedPassword) {
        return hashedPassword
      }
    }
  } catch (error) {
    console.warn('[Auth] Edge Function hash-password failed, falling back to SHA-256:', error)
  }

  // Fallback to SHA-256 (for backward compatibility during migration)
  const encoder = new TextEncoder()
  const data = encoder.encode(password)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  return hashHex
}

/**
 * Verifies a password against a hash using bcrypt via Supabase Edge Function
 * Falls back to SHA-256 comparison if Edge Function is not available
 * 
 * @param password - Plain text password
 * @param hash - Stored password hash (bcrypt or SHA-256)
 * @returns true if password matches, false otherwise
 */
async function verifyPassword(password: string, hash: string): Promise<boolean> {
  // Detect hash type: bcrypt hashes start with $2a$, $2b$, or $2y$
  const isBcryptHash = hash.startsWith('$2a$') || hash.startsWith('$2b$') || hash.startsWith('$2y$')
  
  if (isBcryptHash) {
    // Use Edge Function for bcrypt verification
    try {
      const { supabaseUrl, supabaseAnonKey } = getSupabaseConfig()
      
      const response = await fetch(`${supabaseUrl}/functions/v1/verify-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'apikey': supabaseAnonKey
        },
        body: JSON.stringify({ password, hash })
      })

      if (response.ok) {
        const { valid } = await response.json()
        return valid === true
      }
    } catch (error) {
      console.warn('[Auth] Edge Function verify-password failed, falling back to SHA-256:', error)
    }
  }

  // Fallback to SHA-256 comparison (for backward compatibility)
  const passwordHash = await hashPasswordSHA256(password)
  return passwordHash === hash
}

/**
 * Hashes a password using SHA-256 (legacy method, for backward compatibility)
 * @deprecated Use hashPassword() which uses bcrypt via Edge Function
 */
async function hashPasswordSHA256(password: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(password)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  return hashHex
}

/**
 * Checks if a string is a valid email format
 */
function isEmail(input: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(input.trim())
}

/**
 * Authenticates a tenant admin using email OR username and password
 * Supports both email and username for flexible login
 * @param identifier - Tenant admin email (e.g., "tenant@example.com") OR username (e.g., "admin_bakers")
 * @param password - Plain text password
 * @returns Tenant data if authentication succeeds, null otherwise
 */
export async function authenticateTenantAdmin(identifier: string, password: string) {
  const db = getDb()
  
  // Detect if identifier is email or username
  const isEmailFormat = isEmail(identifier)
  
  let tenant
  
  if (isEmailFormat) {
    // Try email lookup first (user-friendly)
    const { data, error } = await db
      .from('tenants')
      .select('*')
      .eq('email', identifier.toLowerCase().trim())
      .eq('status', 'active') // Only allow active tenants
      .single()
    
    if (error || !data) {
      return null
    }
    tenant = data
  } else {
    // Try username lookup (backward compatibility)
    const { data, error } = await db
      .from('tenants')
      .select('*')
      .eq('admin_username', identifier)
      .eq('status', 'active') // Only allow active tenants
      .single()
    
    if (error || !data) {
      return null
    }
    tenant = data
  }

  // Verify password (supports both bcrypt and SHA-256 for backward compatibility)
  const isValid = await verifyPassword(password, tenant.admin_password_hash)
  if (!isValid) {
    return null
  }

  // Auto-migrate password: If hash is SHA-256, upgrade to bcrypt
  const isBcryptHash = tenant.admin_password_hash.startsWith('$2a$') || 
                       tenant.admin_password_hash.startsWith('$2b$') || 
                       tenant.admin_password_hash.startsWith('$2y$')
  
  if (!isBcryptHash) {
    // Password is SHA-256, migrate to bcrypt
    try {
      const newHash = await hashPassword(password)
      // Verify that we got a bcrypt hash (starts with $2a$, $2b$, or $2y$)
      // If Edge Functions aren't available, hashPassword() will return SHA-256, so skip migration
      const isNewHashBcrypt = newHash && (newHash.startsWith('$2a$') || newHash.startsWith('$2b$') || newHash.startsWith('$2y$'))
      
      if (isNewHashBcrypt && newHash !== tenant.admin_password_hash) {
        // Update password hash to bcrypt (fire and forget - don't block login)
        db.from('tenants')
          .update({ 
            admin_password_hash: newHash,
            updated_at: new Date().toISOString()
          })
          .eq('id', tenant.id)
          .then(() => {
            if (import.meta.env.DEV) {
              console.log(`[Auth] Migrated password for tenant ${tenant.id} from SHA-256 to bcrypt`)
            }
          })
          .catch((error) => {
            console.warn(`[Auth] Failed to migrate password for tenant ${tenant.id}:`, error)
          })
      } else if (!isNewHashBcrypt) {
        // Edge Functions not available, migration skipped
        if (import.meta.env.DEV) {
          console.warn(`[Auth] Skipping password migration for tenant ${tenant.id}: Edge Functions not available`)
        }
      }
    } catch (error) {
      // Migration failed, but login succeeded - log warning
      console.warn(`[Auth] Password migration failed for tenant ${tenant.id}:`, error)
    }
  }

  // Return tenant data (without sensitive fields)
  return {
    id: tenant.id,
    slug: tenant.slug,
    name: tenant.name,
    email: tenant.email,
    plan: tenant.plan,
    vertical: tenant.vertical,
    status: tenant.status,
    admin_portal_url: tenant.admin_portal_url,
    customer_website_url: tenant.customer_website_url
  }
}

/**
 * Fetches tenant by admin username (for login page)
 */
export async function fetchTenantByAdminUsername(username: string) {
  const db = getDb()
  const { data, error } = await db
    .from('tenants')
    .select('id, slug, name, admin_portal_url, status')
    .eq('admin_username', username)
    .single()

  if (error) {
    return null
  }

  return data
}

/**
 * Hashes a password for platform admin using bcrypt via Edge Function
 * Falls back to SHA-256 if Edge Function is not available (backward compatibility)
 * 
 * @param password - Plain text password to hash
 * @returns Hashed password (bcrypt or SHA-256)
 */
async function hashPasswordForPlatform(password: string): Promise<string> {
  // Use the same hashPassword function (uses Edge Function)
  return await hashPassword(password)
}

/**
 * Authenticates a platform admin using email and password
 * @param email - Platform admin email
 * @param password - Plain text password
 * @returns Platform admin data if authentication succeeds, null otherwise
 */
export async function authenticatePlatformAdmin(email: string, password: string) {
  const db = getDb()
  
  // Find platform admin by email
  const { data: admin, error } = await db
    .from('platform_admins')
    .select('id, email, password_hash, name, role, status')
    .eq('email', email.toLowerCase().trim())
    .eq('status', 'active')
    .single()

  if (error || !admin) {
    return null
  }

  // Verify password (supports both bcrypt and SHA-256 for backward compatibility)
  const isValid = await verifyPassword(password, admin.password_hash)
  if (!isValid) {
    return null
  }

  // Auto-migrate password: If hash is SHA-256, upgrade to bcrypt
  const isBcryptHash = admin.password_hash.startsWith('$2a$') || 
                       admin.password_hash.startsWith('$2b$') || 
                       admin.password_hash.startsWith('$2y$')
  
  if (!isBcryptHash) {
    // Password is SHA-256, migrate to bcrypt
    try {
      const newHash = await hashPasswordForPlatform(password)
      // Verify that we got a bcrypt hash (starts with $2a$, $2b$, or $2y$)
      // If Edge Functions aren't available, hashPasswordForPlatform() will return SHA-256, so skip migration
      const isNewHashBcrypt = newHash && (newHash.startsWith('$2a$') || newHash.startsWith('$2b$') || newHash.startsWith('$2y$'))
      
      if (isNewHashBcrypt && newHash !== admin.password_hash) {
        // Update password hash to bcrypt
        await db
          .from('platform_admins')
          .update({ 
            password_hash: newHash,
            last_login_at: new Date().toISOString()
          })
          .eq('id', admin.id)
        
        if (import.meta.env.DEV) {
          console.log(`[Auth] Migrated password for platform admin ${admin.id} from SHA-256 to bcrypt`)
        }
      } else if (!isNewHashBcrypt) {
        // Edge Functions not available, migration skipped
        if (import.meta.env.DEV) {
          console.warn(`[Auth] Skipping password migration for platform admin ${admin.id}: Edge Functions not available`)
        }
        // Update last login timestamp only
        await db
          .from('platform_admins')
          .update({ last_login_at: new Date().toISOString() })
          .eq('id', admin.id)
      }
    } catch (error) {
      // Migration failed, but login succeeded - log warning
      console.warn(`[Auth] Password migration failed for platform admin ${admin.id}:`, error)
      // Update last login timestamp even if migration failed
      await db
        .from('platform_admins')
        .update({ last_login_at: new Date().toISOString() })
        .eq('id', admin.id)
    }
  } else {
    // Update last login timestamp only (password already migrated)
    await db
      .from('platform_admins')
      .update({ last_login_at: new Date().toISOString() })
      .eq('id', admin.id)
  }

  // Return platform admin data (without sensitive fields)
  return {
    id: admin.id,
    email: admin.email,
    name: admin.name,
    role: admin.role || 'platform-admin',
    authenticated: true
  }
}

/**
 * Checks if current user is authenticated as platform admin
 */
export function isPlatformAdminAuthenticated(): boolean {
  if (typeof window === 'undefined') return false
  
  try {
    const authData = localStorage.getItem('platformAdminAuth')
    if (!authData) return false
    
    const auth = JSON.parse(authData)
    return auth.authenticated === true && auth.role === 'platform-admin'
  } catch {
    return false
  }
}

/**
 * Logs out platform admin
 */
export function logoutPlatformAdmin(): void {
  localStorage.removeItem('platformAdminAuth')
}

/**
 * Generates a password reset token for platform admin
 * @param email - Platform admin email
 * @returns Reset token if email exists, null otherwise
 */
export async function generatePasswordResetToken(email: string): Promise<string | null> {
  const db = getDb()
  
  // Find platform admin by email
  const { data: admin, error } = await db
    .from('platform_admins')
    .select('id, email')
    .eq('email', email.toLowerCase().trim())
    .eq('status', 'active')
    .single()

  if (error || !admin) {
    return null
  }

  // Generate reset token (simple UUID-based token)
  const resetToken = crypto.randomUUID()
  const expiresAt = new Date()
  expiresAt.setHours(expiresAt.getHours() + 1) // Token expires in 1 hour

  // Save reset token to database
  await db
    .from('platform_admins')
    .update({
      password_reset_token: resetToken,
      password_reset_expires_at: expiresAt.toISOString()
    })
    .eq('id', admin.id)

  return resetToken
}

/**
 * Resets platform admin password using reset token
 * @param token - Password reset token
 * @param newPassword - New password
 * @returns true if successful, false otherwise
 */
export async function resetPlatformAdminPassword(token: string, newPassword: string): Promise<boolean> {
  const db = getDb()
  
  // Validate password length
  if (!newPassword || newPassword.length < 6) {
    throw new Error('Password must be at least 6 characters long')
  }

  // Find admin by reset token
  const { data: admin, error } = await db
    .from('platform_admins')
    .select('id, password_reset_expires_at')
    .eq('password_reset_token', token)
    .eq('status', 'active')
    .single()

  if (error || !admin) {
    return false
  }

  // Check if token is expired
  if (admin.password_reset_expires_at) {
    const expiresAt = new Date(admin.password_reset_expires_at)
    if (expiresAt < new Date()) {
      return false
    }
  }

  // Hash new password
  const passwordHash = await hashPasswordForPlatform(newPassword)

  // Update password and clear reset token
  const { error: updateError } = await db
    .from('platform_admins')
    .update({
      password_hash: passwordHash,
      password_reset_token: null,
      password_reset_expires_at: null
    })
    .eq('id', admin.id)

  return !updateError
}

/**
 * Changes platform admin password (requires current password)
 * @param email - Platform admin email
 * @param currentPassword - Current password
 * @param newPassword - New password
 * @returns true if successful, false otherwise
 */
export async function changePlatformAdminPassword(
  email: string,
  currentPassword: string,
  newPassword: string
): Promise<boolean> {
  // Validate new password length
  if (!newPassword || newPassword.length < 6) {
    throw new Error('Password must be at least 6 characters long')
  }

  // First authenticate with current password
  const admin = await authenticatePlatformAdmin(email, currentPassword)
  if (!admin) {
    return false
  }

  const db = getDb()
  
  // Hash new password
  const passwordHash = await hashPasswordForPlatform(newPassword)

  // Update password
  const { error } = await db
    .from('platform_admins')
    .update({ password_hash: passwordHash })
    .eq('id', admin.id)

  return !error
}

/**
 * Changes tenant admin password (requires current password)
 * @param tenantId - Tenant ID
 * @param currentPassword - Current password
 * @param newPassword - New password
 * @returns true if successful, false otherwise
 */
export async function changeTenantAdminPassword(
  tenantId: string,
  currentPassword: string,
  newPassword: string
): Promise<boolean> {
  const db = getDb()
  
  // Validate new password length
  if (!newPassword || newPassword.length < 6) {
    throw new Error('Password must be at least 6 characters long')
  }

  // Fetch tenant
  const { data: tenant, error: fetchError } = await db
    .from('tenants')
    .select('admin_password_hash')
    .eq('id', tenantId)
    .single()

  if (fetchError || !tenant) {
    throw new Error('Tenant not found')
  }

  // Verify current password (supports both bcrypt and SHA-256)
  const isValid = await verifyPassword(currentPassword, tenant.admin_password_hash)
  if (!isValid) {
    return false
  }

  // Hash new password
  const newPasswordHash = await hashPassword(newPassword)

  // Update password
  const { error } = await db
    .from('tenants')
    .update({ 
      admin_password_hash: newPasswordHash,
      updated_at: new Date().toISOString()
    })
    .eq('id', tenantId)

  return !error
}

